// Serienbrief (E-POST) aus dem Serienmail-Assistenten: je Empfänger Anschrift + Anschreiben-Text mit gefüllten
// Platzhaltern (dieselben wie in der Serienmail, lib/serienmail/platzhalter.ts) – reine Logik, Tests nebenan.
import { ersetzePlatzhalter } from '../aktionen/platzhalter';
import { htmlZuText } from '../dokumente/briefDaten';
import { baueWerte } from '../serienmail/platzhalter';
import type { Kandidat } from '../serienmail/typen';
import { pruefeEmpfaenger, type Empfaenger } from '../../supabase/functions/epost-brief/logik';

export type PostModus = 'aus' | 'ohneEmail' | 'alle';

export interface SerienBrief { kandidat: Kandidat; anschrift: string; empfaenger: Empfaenger; betreff: string; text: string }

/** Anschrift fürs Fenster: Name, Straße Hausnummer, PLZ Ort. */
export function kandidatAnschrift(k: Kandidat): { text: string; empfaenger: Empfaenger } {
  const strasse = [k.strasse, k.hausnummer].map((x) => (x ?? '').trim()).filter(Boolean).join(' ');
  const zeilen = [k.name.trim(), strasse].filter(Boolean);
  const plz = (k.plz ?? '').trim(), ort = (k.stadt ?? '').trim();
  return { text: [...zeilen, `${plz} ${ort}`.trim()].filter(Boolean).join('\n'), empfaenger: { zeilen, plz, ort } };
}

/**
 * Wer bekommt einen Brief? 'ohneEmail' = nur die ohne E-Mail-Adresse, 'alle' = jeder Kandidat einmal.
 * Ohne vollständige Anschrift landet er in `ohneAnschrift` (wird angezeigt, nicht verschickt).
 */
export function waehleBriefEmpfaenger(modus: PostModus, alle: Kandidat[], ohneEmail: Kandidat[]): { briefe: Kandidat[]; ohneAnschrift: Kandidat[] } {
  if (modus === 'aus') return { briefe: [], ohneAnschrift: [] };
  const basis = modus === 'alle' ? alle : ohneEmail;
  const gesehen = new Set<string>();
  const briefe: Kandidat[] = [], ohneAnschrift: Kandidat[] = [];
  for (const k of basis) {
    const schluessel = `${k.owner_type}:${k.owner_id}`;
    if (gesehen.has(schluessel)) continue;
    gesehen.add(schluessel);
    (pruefeEmpfaenger(kandidatAnschrift(k).empfaenger).length ? ohneAnschrift : briefe).push(k);
  }
  return { briefe, ohneAnschrift };
}

/** Betreff + Text des Anschreibens für EINEN Empfänger (Platzhalter gefüllt, HTML → Text). */
export function serienBrief(k: Kandidat, html: string, betreff: string, absender: { name: string; email: string }, heute: string): SerienBrief {
  const werte = baueWerte(k, absender, heute);
  const { text: anschrift, empfaenger } = kandidatAnschrift(k);
  const text = mitGruss(htmlZuText(ersetzePlatzhalter(html, werte)));
  return { kandidat: k, anschrift, empfaenger, betreff: ersetzePlatzhalter(betreff, werte).trim(), text: persoenlicheAnrede(text, werte['empfaenger.briefanrede']) };
}

/** Steht vorn nur die neutrale Anrede, wird sie durch die persönliche ersetzt („Sehr geehrte Frau Berg,“). */
export function persoenlicheAnrede(text: string, anrede: string | undefined): string {
  if (!anrede || /damen und herren/i.test(anrede)) return text;
  return text.replace(/^\s*Sehr geehrte Damen und Herren,?/i, anrede);
}

/** In der Serienmail steht der Gruß meist in der Signatur – im Brief fehlt er dann. Nur ergänzen, wenn keiner da ist. */
export function mitGruss(text: string): string {
  if (/(grüße|grüßen|gruß)(?![a-zäöüß])/i.test(text.split('\n').slice(-6).join(' '))) return text;
  return `${text.trimEnd()}\n\nMit freundlichen Grüßen\n\nImmobilienmanagement Schwede`;
}
